import { Plus, Trash2 } from 'lucide-react'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { Field, Notice, StateSelect, StepCard, StepTitle, SubHeading } from '../../components/ui'
import { miscStateName } from '../../data/miscHandledStates'
import type { BusinessEntity, Drlp } from '../../types/onboarding'

/**
 * Design manual p.5 — DRLP Assignment. No company field: the entity is already the context.
 * Multiple DRLPs with a state assignment matrix (e.g. one DRLP everywhere except Florida, or five DRLPs on one entity).
 */
export function EntityDrlps({ entity }: { entity: BusinessEntity }) {
  const { dispatch } = useOnboarding()
  const update = (drlpId: string, patch: Partial<Omit<Drlp, 'id'>>) =>
    dispatch({ type: 'UPDATE_DRLP', entityId: entity.id, drlpId, patch })
  const toggleState = (drlpId: string, stateCode: string) =>
    dispatch({ type: 'TOGGLE_DRLP_STATE', entityId: entity.id, drlpId, stateCode })

  const states = entity.statesToHandle

  return (
    <StepCard>
      <StepTitle subtitle="Each business entity must designate at least one DRLP.">
        Designated Responsible Licensed Producer (DRLP)
      </StepTitle>

      <Stack spacing={2}>
        {entity.drlps.map((d, i) => (
          <Box key={d.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                DRLP {i + 1}
                {d.fullName ? ` — ${d.fullName}` : ''}
              </Typography>
              <Tooltip title={entity.drlps.length <= 1 ? 'At least one DRLP is required' : 'Remove DRLP'}>
                <span>
                  <IconButton
                    aria-label={`Remove DRLP ${i + 1}`}
                    disabled={entity.drlps.length <= 1}
                    onClick={() => dispatch({ type: 'REMOVE_DRLP', entityId: entity.id, drlpId: d.id })}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
              <Field label="Full Name" required value={d.fullName} onChange={(e) => update(d.id, { fullName: e.target.value })} />
              <Field label="Job Title" required value={d.jobTitle} onChange={(e) => update(d.id, { jobTitle: e.target.value })} />
              <StateSelect label="Resident State" required value={d.residentState} onChange={(code) => update(d.id, { residentState: code })} />
              <Field
                label="Surplus Lines License Number"
                required
                value={d.slLicenseNumber}
                onChange={(e) => update(d.id, { slLicenseNumber: e.target.value })}
              />
              <Field label="NPN (Optional)" value={d.npn} onChange={(e) => update(d.id, { npn: e.target.value })} />
            </Box>
          </Box>
        ))}

        <Box>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => dispatch({ type: 'ADD_DRLP', entityId: entity.id })}
          >
            Add DRLP
          </Button>
        </Box>

        <Box>
          <SubHeading>Assigned States</SubHeading>
          <Typography sx={{ fontSize: 14, color: '#475569', mb: 1.5 }}>
            Check the DRLP responsible for each state MISC will handle for this entity.
          </Typography>
          {states.length === 0 ? (
            <Notice tone="warning">Select states for this entity first — the assignment matrix lists the states MISC will handle.</Notice>
          ) : (
            <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Table size="small" sx={{ minWidth: 320 + entity.drlps.length * 140 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}>State</TableCell>
                    {entity.drlps.map((d, i) => (
                      <TableCell key={d.id} align="center" sx={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                        {d.fullName || `DRLP ${i + 1}`}
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Button
                            type="button"
                            size="small"
                            sx={{ minWidth: 0, p: 0, fontSize: 11 }}
                            onClick={() => update(d.id, { assignedStates: [...states] })}
                          >
                            All
                          </Button>
                          <Button
                            type="button"
                            size="small"
                            sx={{ minWidth: 0, p: 0, fontSize: 11, color: '#64748b' }}
                            onClick={() => update(d.id, { assignedStates: [] })}
                          >
                            None
                          </Button>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {states.map((code) => {
                    const assigned = entity.drlps.some((d) => d.assignedStates.includes(code))
                    return (
                      <TableRow key={code} sx={assigned ? undefined : { bgcolor: '#fffbeb' }}>
                        <TableCell sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                          {miscStateName(code)}
                          {!assigned ? (
                            <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: '#92400e' }}>
                              No DRLP
                            </Box>
                          ) : null}
                        </TableCell>
                        {entity.drlps.map((d, i) => (
                          <TableCell key={d.id} align="center" padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={d.assignedStates.includes(code)}
                              onChange={() => toggleState(d.id, code)}
                              inputProps={{ 'aria-label': `${d.fullName || `DRLP ${i + 1}`} for ${miscStateName(code)}` }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Stack>
    </StepCard>
  )
}
